import { getWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ORDER_VALUE } from '@spo/logic';
import { getCurrentLanguage } from '@spo/services/i18n';
import { PosterSkeleton } from '@spo/storybook';
import { SpoPosterTableResponse } from '@spo/types';
import { VpIcon } from '@vtmn-play/react';
import { useRoles } from '../../contexts/RolesContext';
import { usePoster } from '../../hooks/usePosterTable';
import { ErrorPage } from '../errorPage';
import GoBackLink from '../goBackLink/GoBackLink';
import { HeaderDisplay } from '../headerDisplay';
import { Layout } from '../layout';
import { FirstLevel } from './FirstLevel';
import './offerPoster.scss';

const OfferPosterDisplay = ({
  posterOffer,
}: {
  posterOffer: SpoPosterTableResponse;
}) => {
  const { t } = useTranslation();
  return posterOffer.length === 0 ? (
    <div
      className="poster-no-result"
      data-testid="poster_no-result-placeholder"
    >
      {t('no-result')}
      <VpIcon name="emotion-sad" />
    </div>
  ) : (
    posterOffer?.map((poster, index) => (
      <FirstLevel
        key={`${index} - ${poster.smSegmentation.label}`}
        content={poster}
      />
    ))
  );
};

export const OfferPoster = () => {
  const { t } = useTranslation();

  const params = new URLSearchParams(document.location.search);
  const sportId = Number(params.get('sportId'));
  const natureId = Number(params.get('natureId'));
  const offerDate = params.get('offerDate');

  const { hasRoleVisualization } = useRoles();

  const formatOfferDate = (offerDate: string) =>
    `W${getWeek(new Date(offerDate), { weekStartsOn: 0 })} - ${offerDate}`;

  const filters = {
    productManagerIds: [],
    productNatureIds: [natureId],
    retailFamilyIds: [],
    segmentationIds: [],
    sportIds: [sportId],
    status: [],
  };

  const { data: superModelsPosterTable, isFetching } = usePoster(
    offerDate as string,
    { start: 0, end: 1000 },
    filters,
    {
      id: '',
      order: ORDER_VALUE.EMPTY,
    },
    getCurrentLanguage(),
  );

  return (
    <Layout>
      {hasRoleVisualization ? (
        <div className="vtmn-w-full vtmn-flex vtmn-flex-col">
          <HeaderDisplay />
          <div data-testid="offer-poster">
            <GoBackLink path="/personal-offer" />
            <div className="offer-poster-header">
              <div className="offer-poster-title">
                {t('offer-poster.title')}
              </div>
              <div className="offer-poster-date">
                {offerDate
                  ? formatOfferDate(offerDate)
                  : t('offer-poster.date-error')}
              </div>
            </div>
            <div className="offer-poster-scroll-y-container">
              <div
                data-testid="offer-poster-content"
                className="offer-poster-content"
              >
                <div
                  className="first-level-container"
                  data-testid="first-level_container"
                >
                  {!isFetching && superModelsPosterTable ? (
                    <OfferPosterDisplay posterOffer={superModelsPosterTable} />
                  ) : (
                    <PosterSkeleton />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ErrorPage
          errorPageTitle={t('no-access-page.title')}
          errorMessage={t('no-access-page.subtitle')}
        />
      )}
    </Layout>
  );
};
